import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  Copy,
  HeartPulse,
  ListChecks,
  Loader2,
  MessageSquareHeart,
  Play,
  Plus,
  RefreshCw,
  Send,
  SkipForward,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Brand } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { useSegments } from '@/features/vault/api';
import { useContacts, useMyDay } from '@/features/crm/api';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);

// ══ Surveys ══════════════════════════════════════════════════════════════════

type SurveyType = 'NPS' | 'CSAT' | 'CES';
type Survey = {
  id: string;
  type: SurveyType;
  name: string;
  question: string;
  followUp: string | null;
  thankYou: string | null;
  isActive: boolean;
  sendOnTicketClose: boolean;
  link: string;
  responses: number;
  score: number | null;
  createdAt: string;
};
type Results = {
  survey: Survey;
  responses: number;
  score: number | null;
  average?: number;
  distribution: { score: number; count: number }[];
  breakdown: { promoters: number; passives: number; detractors: number } | null;
  recent: { id: string; score: number; comment: string | null; email: string | null; createdAt: string; contact: { id: string; name: string; company: string | null } | null }[];
};

const TYPE_INFO: Record<SurveyType, { label: string; scale: string; scoreLabel: (score: number | null) => string }> = {
  NPS: { label: 'Net Promoter Score', scale: '0–10', scoreLabel: (score) => (score === null ? '--' : `${score > 0 ? '+' : ''}${score}`) },
  CSAT: { label: 'Customer satisfaction', scale: '1–5', scoreLabel: (score) => (score === null ? '--' : `${score}%`) },
  CES: { label: 'Customer effort', scale: '1–7', scoreLabel: (score) => (score === null ? '--' : String(score)) },
};

export function SurveysPage() {
  const t = useT();
  const navigate = useNavigate();
  const { data: surveys = [], isLoading } = useQuery({ queryKey: ['surveys'], queryFn: () => apiGet<Survey[]>('/surveys') });
  const [creating, setCreating] = useState(false);
  return (
    <div>
      <PageHeader
        title="Feedback surveys"
        description="Net Promoter Score, customer satisfaction and customer effort. Send by email, ask automatically when a ticket closes, or share the link."
        actions={
          <Can permission="service.survey.create">
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('Create survey')}
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : surveys.length === 0 ? (
        <EmptyState
          icon={<MessageSquareHeart className="h-6 w-6" />}
          title="No surveys yet"
          description="Start with an NPS survey: one question, answered in a click, and you know who loves you."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('Create survey')}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {surveys.map((survey) => (
            <button
              key={survey.id}
              type="button"
              onClick={() => navigate(`/service/surveys/${survey.id}`)}
              className="rounded-lg border bg-card p-5 text-start transition-colors hover:border-primary/50"
            >
              <div className="flex items-center justify-between gap-2">
                <Badge variant="secondary">{survey.type}</Badge>
                {!survey.isActive && <Badge variant="warning">{t('Off')}</Badge>}
              </div>
              <p className="mt-3 font-bold">{survey.name}</p>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{survey.question}</p>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold">{TYPE_INFO[survey.type].scoreLabel(survey.score)}</p>
                  <p className="text-xs text-muted-foreground">{t(TYPE_INFO[survey.type].label)}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {survey.responses} {t('responses')}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      {creating && <SurveyDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function SurveyDialog({ survey, onClose }: { survey?: Survey; onClose: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    type: survey?.type ?? ('NPS' as SurveyType),
    name: survey?.name ?? '',
    question: survey?.question ?? '',
    followUp: survey?.followUp ?? '',
    thankYou: survey?.thankYou ?? '',
    sendOnTicketClose: survey?.sendOnTicketClose ?? false,
    isActive: survey?.isActive ?? true,
  });
  const save = useMutation({
    mutationFn: () => (survey ? apiPatch<Survey>(`/surveys/${survey.id}`, { ...form, type: undefined }) : apiPost<Survey>('/surveys', form)),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['surveys'] });
      onClose();
      if (!survey) navigate(`/service/surveys/${saved.id}`);
    },
    onError: (error) => failed(error, 'Could not save the survey'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{survey ? t('Edit survey') : t('Create survey')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!survey && (
            <div className="grid grid-cols-3 gap-2">
              {(['NPS', 'CSAT', 'CES'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setForm({ ...form, type })}
                  className={cn('rounded-lg border p-3 text-start', form.type === type ? 'border-primary bg-primary/[0.06] ring-1 ring-primary' : 'hover:bg-accent')}
                >
                  <p className="font-bold">{type}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(TYPE_INFO[type].label)} · {TYPE_INFO[type].scale}
                  </p>
                </button>
              ))}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t('Name')}</Label>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder={t('e.g. Quarterly NPS')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Question')}</Label>
            <Textarea rows={2} value={form.question} onChange={(event) => setForm({ ...form, question: event.target.value })} placeholder={t('Leave blank for the standard question')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Follow-up question')}</Label>
            <Input value={form.followUp} onChange={(event) => setForm({ ...form, followUp: event.target.value })} placeholder={t('What is the main reason for your score?')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Thank-you message')}</Label>
            <Input value={form.thankYou} onChange={(event) => setForm({ ...form, thankYou: event.target.value })} placeholder={t('Thank you for your feedback!')} />
          </div>
          <label className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
            <span>
              <span className="font-semibold">{t('Send when a ticket is closed')}</span>
              <span className="block text-xs text-muted-foreground">{t('The customer on the ticket gets this survey by email.')}</span>
            </span>
            <Switch checked={form.sendOnTicketClose} onCheckedChange={(value) => setForm({ ...form, sendOnTicketClose: value })} />
          </label>
          {survey && (
            <label className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
              <span className="font-semibold">{t('Survey is on')}</span>
              <Switch checked={form.isActive} onCheckedChange={(value) => setForm({ ...form, isActive: value })} />
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!form.name.trim() || save.isPending}>
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SurveyResultsPage() {
  const t = useT();
  const { surveyId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['surveys', surveyId], queryFn: () => apiGet<Results>(`/surveys/${surveyId}`) });
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const remove = useMutation({
    mutationFn: () => apiDelete(`/surveys/${surveyId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['surveys'] });
      navigate('/service/surveys');
    },
  });
  if (isLoading || !data) return <PageLoader />;
  const { survey } = data;
  const max = Math.max(1, ...data.distribution.map((row) => row.count));
  return (
    <div>
      <PageHeader
        title={survey.name}
        breadcrumbs={[{ label: 'Feedback surveys', to: '/service/surveys' }, { label: survey.name }]}
        description={survey.question}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(survey.link);
                toast.success(t('Link copied'));
              }}
            >
              <Copy /> {t('Copy link')}
            </Button>
            <Can permission="service.survey.update">
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t('Edit')}
              </Button>
              <Button onClick={() => setSending(true)}>
                <Send /> {t('Send')}
              </Button>
            </Can>
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        <div className="space-y-4">
          <div className="rounded-lg border bg-card p-5">
            <p className="text-sm text-muted-foreground">{t(TYPE_INFO[survey.type].label)}</p>
            <p className="mt-1 text-5xl font-bold">{TYPE_INFO[survey.type].scoreLabel(data.score)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.responses} {t('responses')}
              {data.average !== undefined && ` · ${t('average')} ${data.average}`}
            </p>
            {data.breakdown && (
              <div className="mt-4 space-y-1.5 text-sm">
                {[
                  ['Promoters (9–10)', data.breakdown.promoters, 'bg-emerald-500'],
                  ['Passives (7–8)', data.breakdown.passives, 'bg-amber-400'],
                  ['Detractors (0–6)', data.breakdown.detractors, 'bg-red-500'],
                ].map(([label, count, colour]) => (
                  <div key={String(label)} className="flex items-center gap-2">
                    <span className={cn('h-2.5 w-2.5 rounded-full', String(colour))} />
                    <span className="flex-1">{t(String(label))}</span>
                    <span className="tabular font-semibold">{String(count)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border bg-card p-5">
            <p className="mb-3 flex items-center gap-1.5 text-sm font-bold">
              <BarChart3 className="h-4 w-4" /> {t('Answers by score')}
            </p>
            <div className="flex h-32 items-end gap-1">
              {data.distribution.map((row) => (
                <div key={row.score} className="flex flex-1 flex-col items-center gap-1">
                  <div
                    className={cn(
                      'w-full rounded-t',
                      survey.type === 'NPS' ? (row.score >= 9 ? 'bg-emerald-500' : row.score >= 7 ? 'bg-amber-400' : 'bg-red-500') : 'bg-primary',
                    )}
                    style={{ height: `${(row.count / max) * 100}%`, minHeight: row.count ? 4 : 1 }}
                    title={`${row.count}`}
                  />
                  <span className="text-[10px] text-muted-foreground">{row.score}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="break-all rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{survey.link}</p>
        </div>
        <div className="rounded-lg border bg-card">
          <p className="border-b px-5 py-3 font-bold">{t('Latest answers')}</p>
          {data.recent.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">{t('No answers yet. Send the survey or share its link.')}</p>
          ) : (
            <ul className="divide-y">
              {data.recent.map((row) => (
                <li key={row.id} className="flex gap-3 px-5 py-3">
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
                      survey.type === 'NPS' ? (row.score >= 9 ? 'bg-emerald-500' : row.score >= 7 ? 'bg-amber-400' : 'bg-red-500') : row.score >= 4 ? 'bg-emerald-500' : 'bg-amber-500',
                    )}
                  >
                    {row.score}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {row.contact ? (
                        <Link to={`/crm/contacts/${row.contact.id}`} className="text-link hover:underline">
                          {row.contact.name}
                        </Link>
                      ) : (
                        (row.email ?? t('Anonymous'))
                      )}
                      {row.contact?.company && <span className="font-normal text-muted-foreground"> · {row.contact.company}</span>}
                    </p>
                    {row.comment && <p className="mt-0.5 text-sm">{row.comment}</p>}
                    <p className="mt-0.5 text-xs text-muted-foreground">{timeAgo(row.createdAt)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Can permission="service.survey.delete">
        <button
          type="button"
          className="mt-6 inline-flex items-center gap-1 text-sm text-destructive hover:underline"
          onClick={() => window.confirm(t('Delete this survey? Its answers are kept on the contacts.')) && remove.mutate()}
        >
          <Trash2 className="h-4 w-4" /> {t('Delete survey')}
        </button>
      </Can>
      {editing && <SurveyDialog survey={survey} onClose={() => { setEditing(false); void queryClient.invalidateQueries({ queryKey: ['surveys', surveyId] }); }} />}
      {sending && <SendSurveyDialog survey={survey} onClose={() => setSending(false)} />}
    </div>
  );
}

function SendSurveyDialog({ survey, onClose }: { survey: Survey; onClose: () => void }) {
  const t = useT();
  const { data: segments = [] } = useSegments();
  const [mode, setMode] = useState<'segment' | 'contacts'>('segment');
  const [segmentId, setSegmentId] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const { data: contacts } = useContacts({ q: search || undefined, limit: 50 }, mode === 'contacts');
  const send = useMutation({
    mutationFn: () => apiPost<{ sent: number; skipped: number }>(`/surveys/${survey.id}/send`, mode === 'segment' ? { segmentId } : { contactIds: picked }),
    onSuccess: (result) => {
      toast.success(t('Survey sent to {count} people', { count: result.sent }) + (result.skipped ? ` · ${t('{count} without an email', { count: result.skipped })}` : ''));
      onClose();
    },
    onError: (error) => failed(error, 'Could not send the survey'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Send "{name}"', { name: survey.name })}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 rounded-md border p-1">
          {(['segment', 'contacts'] as const).map((item) => (
            <button key={item} type="button" onClick={() => setMode(item)} className={cn('flex-1 rounded py-1.5 text-sm font-semibold', mode === item ? 'bg-muted' : 'text-muted-foreground')}>
              {item === 'segment' ? t('A segment') : t('Chosen contacts')}
            </button>
          ))}
        </div>
        {mode === 'segment' ? (
          <Select value={segmentId} onChange={(event) => setSegmentId(event.target.value)}>
            <option value="">{t('Choose a segment…')}</option>
            {segments.map((segment) => (
              <option key={segment.id} value={segment.id}>
                {segment.name}
              </option>
            ))}
          </Select>
        ) : (
          <div className="space-y-2">
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('Search contacts')} />
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
              {(contacts?.data ?? []).map((contact) => (
                <label key={contact.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent">
                  <Checkbox checked={picked.includes(contact.id)} onCheckedChange={(checked) => setPicked(checked ? [...picked, contact.id] : picked.filter((id) => id !== contact.id))} />
                  <span className="flex-1 truncate">{contact.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{contact.email ?? t('no email')}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {picked.length} {t('selected')}
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => send.mutate()} disabled={send.isPending || (mode === 'segment' ? !segmentId : picked.length === 0)}>
            <Send /> {t('Send survey')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** /s/:token — where the customer answers. */
export function PublicSurveyPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const ref = params.get('r') ?? undefined;
  const preset = params.get('score');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-survey', token],
    queryFn: () => apiGet<{ type: SurveyType; question: string; followUp: string | null; thankYou: string | null; organization: { name: string }; scale: { min: number; max: number } }>(`/public/surveys/${token}`),
    retry: false,
  });
  const [score, setScore] = useState<number | null>(preset !== null ? Number(preset) : null);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const submit = useMutation({
    mutationFn: () => apiPost<{ thankYou: string }>(`/public/surveys/${token}`, { score, comment: comment || undefined, email: ref ? undefined : email || undefined, ref }),
    onError: (error) => failed(error, 'Could not send your answer'),
  });
  const scores = data ? Array.from({ length: data.scale.max - data.scale.min + 1 }, (_, index) => data.scale.min + index) : [];
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fff6f2] px-4 py-10" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-xl rounded-2xl border bg-white p-6 text-slate-900 shadow-brand-lg sm:p-8">
        {isLoading ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        ) : isError || !data ? (
          <p className="text-center text-slate-600">This survey is closed.</p>
        ) : submit.isSuccess ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <p className="mt-4 text-xl font-bold">{submit.data.thankYou}</p>
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-500">{data.organization.name}</p>
            <h1 className="mt-2 text-xl font-bold leading-snug">{data.question}</h1>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {scores.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScore(value)}
                  className={cn(
                    'h-11 min-w-11 flex-1 rounded-lg border text-base font-bold transition-colors',
                    score === value ? 'border-primary bg-primary text-white' : 'border-slate-200 hover:border-primary hover:text-primary',
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="mt-1.5 flex justify-between text-xs text-slate-500">
              <span>{data.type === 'NPS' ? 'Not at all likely' : data.type === 'CES' ? 'Very difficult' : 'Very unsatisfied'}</span>
              <span>{data.type === 'NPS' ? 'Extremely likely' : data.type === 'CES' ? 'Very easy' : 'Very satisfied'}</span>
            </div>
            {score !== null && (
              <div className="mt-5 space-y-3">
                {data.followUp && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold">{data.followUp}</label>
                    <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-3 text-sm outline-none focus:border-primary" />
                  </div>
                )}
                {!ref && (
                  <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="Your email (optional)" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-primary" />
                )}
                <Button className="w-full" onClick={() => submit.mutate()} disabled={submit.isPending}>
                  {submit.isPending ? <Loader2 className="animate-spin" /> : <Check />} Send
                </Button>
              </div>
            )}
          </>
        )}
        <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
          Powered by <Brand size="sm" className="scale-75" />
        </div>
      </div>
    </div>
  );
}

// ══ Customer success ═════════════════════════════════════════════════════════

type HealthRow = {
  id: string;
  name: string;
  type: string;
  lifecycleStage: string;
  healthScore: number | null;
  healthFactors: { label: string; points: number; detail: string }[] | null;
  healthUpdatedAt: string | null;
  owner: { id: string; name: string } | null;
};

export function CustomerSuccessPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const [band, setBand] = useState<'' | 'at_risk' | 'neutral' | 'healthy'>('');
  const { data, isLoading } = useQuery({
    queryKey: ['customer-success', band],
    queryFn: () => apiGet<{ counts: { atRisk: number; neutral: number; healthy: number }; companies: HealthRow[] }>('/customer-success', { params: band ? { band } : undefined }),
  });
  const recalc = useMutation({
    mutationFn: () => apiPost<{ updated: number }>('/customer-success/recalculate', {}),
    onSuccess: (result) => {
      toast.success(t('{count} companies scored', { count: result.updated }));
      void queryClient.invalidateQueries({ queryKey: ['customer-success'] });
    },
    onError: (error) => failed(error, 'Could not recalculate'),
  });
  const [open, setOpen] = useState<string | null>(null);
  const colour = (score: number | null) => (score === null ? 'bg-muted text-muted-foreground' : score < 40 ? 'bg-red-500 text-white' : score < 70 ? 'bg-amber-400 text-slate-900' : 'bg-emerald-500 text-white');
  return (
    <div>
      <PageHeader
        title="Customer success"
        description="Every company scored 0–100 from engagement, tickets, overdue invoices, survey answers and recent wins. Scores refresh every night."
        actions={
          <Can permission="crm.company.update">
            <Button variant="outline" onClick={() => recalc.mutate()} disabled={recalc.isPending}>
              <RefreshCw className={cn(recalc.isPending && 'animate-spin')} /> {t('Recalculate now')}
            </Button>
          </Can>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {[
          ['at_risk', 'At risk', data?.counts.atRisk, 'border-red-300 bg-red-50 dark:bg-red-500/10'],
          ['neutral', 'Needs attention', data?.counts.neutral, 'border-amber-300 bg-amber-50 dark:bg-amber-500/10'],
          ['healthy', 'Healthy', data?.counts.healthy, 'border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10'],
        ].map(([key, label, count, style]) => (
          <button
            key={String(key)}
            type="button"
            onClick={() => setBand(band === key ? '' : (key as typeof band))}
            className={cn('rounded-lg border p-4 text-start transition-shadow', String(style), band === key && 'ring-2 ring-foreground')}
          >
            <p className="text-sm font-semibold">{t(String(label))}</p>
            <p className="mt-1 text-3xl font-bold">{count ?? '--'}</p>
          </button>
        ))}
      </div>
      {isLoading ? (
        <PageLoader />
      ) : !data?.companies.length ? (
        <EmptyState icon={<HeartPulse className="h-6 w-6" />} title="No health scores yet" description="Press Recalculate now to score every company, or wait for tonight's run." />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          {data.companies.map((company) => (
            <div key={company.id} className="border-b last:border-0">
              <button type="button" onClick={() => setOpen(open === company.id ? null : company.id)} className="flex w-full items-center gap-3 px-4 py-3 text-start hover:bg-muted/40">
                <span className={cn('flex h-10 w-12 shrink-0 items-center justify-center rounded-md text-base font-bold', colour(company.healthScore))}>{company.healthScore ?? '--'}</span>
                <span className="min-w-0 flex-1">
                  <Link to={`/crm/companies/${company.id}`} onClick={(event) => event.stopPropagation()} className="text-link font-semibold hover:underline">
                    {company.name}
                  </Link>
                  <span className="block truncate text-xs text-muted-foreground">
                    {(company.healthFactors ?? []).map((factor) => factor.label).join(' · ') || t('Not scored yet')}
                  </span>
                </span>
                <span className="hidden text-sm text-muted-foreground sm:block">{company.owner?.name ?? t('No owner')}</span>
              </button>
              {open === company.id && company.healthFactors && (
                <div className="grid gap-2 bg-muted/30 px-4 py-3 sm:grid-cols-2">
                  {company.healthFactors.map((factor) => (
                    <div key={factor.label} className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
                      <span className={cn('tabular w-10 shrink-0 font-bold', factor.points > 0 ? 'text-emerald-600' : factor.points < 0 ? 'text-red-600' : 'text-muted-foreground')}>
                        {factor.points > 0 ? `+${factor.points}` : factor.points}
                      </span>
                      <span className="min-w-0">
                        <span className="font-semibold">{t(factor.label)}</span>
                        <span className="block truncate text-xs text-muted-foreground">{factor.detail}</span>
                      </span>
                    </div>
                  ))}
                  {company.healthUpdatedAt && <p className="text-xs text-muted-foreground sm:col-span-2">{t('Scored')} {timeAgo(company.healthUpdatedAt)}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══ Task queues ══════════════════════════════════════════════════════════════

type Queue = { id: string; name: string; isShared: boolean; ownerId: string; open: number };
type QueueTask = {
  id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  dueAt: string | null;
  entityType: string | null;
  entityId: string | null;
  recordName: string | null;
  assignee: { id: string; name: string } | null;
};

const RECORD_PATH: Record<string, string> = { contact: 'contacts', company: 'companies', lead: 'leads', deal: 'deals' };

export function TaskQueuesPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: queues = [], isLoading } = useQuery({ queryKey: ['task-queues'], queryFn: () => apiGet<Queue[]>('/task-queues') });
  const [active, setActive] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [working, setWorking] = useState(false);
  const [adding, setAdding] = useState(false);
  const current = queues.find((queue) => queue.id === active) ?? queues[0];
  const { data: tasks = [] } = useQuery({ queryKey: ['task-queues', current?.id, 'tasks'], queryFn: () => apiGet<QueueTask[]>(`/task-queues/${current!.id}/tasks`), enabled: Boolean(current) });
  const create = useMutation({
    mutationFn: () => apiPost<Queue>('/task-queues', { name }),
    onSuccess: (queue) => {
      setName('');
      setActive(queue.id);
      void queryClient.invalidateQueries({ queryKey: ['task-queues'] });
    },
    onError: (error) => failed(error, 'Could not create the queue'),
  });
  const removeQueue = useMutation({
    mutationFn: (id: string) => apiDelete(`/task-queues/${id}`),
    onSuccess: () => {
      setActive(null);
      void queryClient.invalidateQueries({ queryKey: ['task-queues'] });
    },
  });

  if (working && current) return <QueueRunner queue={current} tasks={tasks} onExit={() => setWorking(false)} />;

  return (
    <div>
      <PageHeader title="Task queues" description="Group tasks into a queue and work through them one after another — calls, follow-ups, renewals." />
      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <div className="space-y-2">
          {queues.map((queue) => (
            <button
              key={queue.id}
              type="button"
              onClick={() => setActive(queue.id)}
              className={cn('flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm font-semibold', current?.id === queue.id ? 'border-primary bg-primary/[0.06]' : 'bg-card hover:bg-accent')}
            >
              <span className="truncate">{queue.name}</span>
              <span className="rounded-full bg-muted px-2 text-xs">{queue.open}</span>
            </button>
          ))}
          <form
            className="flex gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={t('New queue name')} className="h-9" />
            <Button size="icon" type="submit" disabled={!name.trim()}>
              <Plus />
            </Button>
          </form>
        </div>
        {isLoading ? (
          <PageLoader />
        ) : !current ? (
          <EmptyState icon={<ListChecks className="h-6 w-6" />} title="No queues yet" description="Create a queue, add tasks to it, then press Start to work through them." />
        ) : (
          <div className="rounded-lg border bg-card">
            <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
              <p className="font-bold">{current.name}</p>
              <span className="text-sm text-muted-foreground">
                {tasks.length} {t('open tasks')}
              </span>
              <div className="ms-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                  <Plus /> {t('Add tasks')}
                </Button>
                <Button size="sm" onClick={() => setWorking(true)} disabled={tasks.length === 0}>
                  <Play /> {t('Start queue')}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => window.confirm(t('Delete this queue? The tasks stay.')) && removeQueue.mutate(current.id)}>
                  <Trash2 />
                </Button>
              </div>
            </div>
            {tasks.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted-foreground">{t('No open tasks in this queue.')}</p>
            ) : (
              <ul className="divide-y">
                {tasks.map((task) => (
                  <li key={task.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <Badge variant="secondary">{task.type.replace('_', ' ')}</Badge>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{task.title}</span>
                      {task.recordName && <span className="block truncate text-xs text-muted-foreground">{task.recordName}</span>}
                    </span>
                    <span className={cn('text-xs', task.dueAt && new Date(task.dueAt) < new Date() ? 'font-semibold text-destructive' : 'text-muted-foreground')}>
                      {task.dueAt ? formatDate(task.dueAt) : t('No due date')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {adding && current && <AddToQueueDialog queue={current} onClose={() => setAdding(false)} />}
    </div>
  );
}

function AddToQueueDialog({ queue, onClose }: { queue: Queue; onClose: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: day } = useMyDay();
  const candidates = useMemo(() => {
    const all = [...(day?.overdue ?? []), ...(day?.today ?? []), ...(day?.upcoming ?? [])] as { id: string; title: string; dueAt?: string | null }[];
    const seen = new Set<string>();
    return all.filter((task) => (seen.has(task.id) ? false : (seen.add(task.id), true)));
  }, [day]);
  const [picked, setPicked] = useState<string[]>([]);
  const add = useMutation({
    mutationFn: () => apiPost(`/task-queues/${queue.id}/tasks`, { taskIds: picked }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-queues'] });
      onClose();
    },
    onError: (error) => failed(error, 'Could not add the tasks'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Add tasks to "{name}"', { name: queue.name })}</DialogTitle>
        </DialogHeader>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {candidates.length === 0 && <p className="text-sm text-muted-foreground">{t('No open tasks on your day. Create tasks from records or with bulk actions.')}</p>}
          {candidates.map((task) => (
            <label key={task.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent">
              <Checkbox checked={picked.includes(task.id)} onCheckedChange={(checked) => setPicked(checked ? [...picked, task.id] : picked.filter((id) => id !== task.id))} />
              <span className="flex-1 truncate">{task.title}</span>
              {task.dueAt && <span className="text-xs text-muted-foreground">{formatDate(task.dueAt)}</span>}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => add.mutate()} disabled={!picked.length || add.isPending}>
            {t('Add {count}', { count: picked.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Working a queue: one task at a time, with its record, done or skip. */
function QueueRunner({ queue, tasks, onExit }: { queue: Queue; tasks: QueueTask[]; onExit: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(0);
  const task = tasks[index];
  const complete = useMutation({
    mutationFn: (id: string) => apiPatch(`/tasks/${id}`, { status: 'DONE' }),
    onSuccess: () => {
      setDone((value) => value + 1);
      setIndex((value) => value + 1);
    },
    onError: (error) => failed(error, 'Could not complete the task'),
  });
  useEffect(() => () => void queryClient.invalidateQueries({ queryKey: ['task-queues'] }), [queryClient]);
  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between">
        <button type="button" onClick={onExit} className="text-link inline-flex items-center gap-1 text-sm font-semibold hover:underline">
          <ArrowLeft className="h-4 w-4" /> {queue.name}
        </button>
        <span className="text-sm text-muted-foreground">
          {Math.min(index + 1, tasks.length)} / {tasks.length} · {done} {t('done')}
        </span>
      </div>
      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${(Math.min(index, tasks.length) / Math.max(1, tasks.length)) * 100}%` }} />
      </div>
      {!task ? (
        <div className="rounded-xl border bg-card p-10 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
          <p className="mt-3 text-xl font-bold">{t('Queue finished')}</p>
          <p className="text-muted-foreground">{t('{count} tasks done.', { count: done })}</p>
          <Button className="mt-5" onClick={onExit}>
            {t('Back to queues')}
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border bg-card p-6">
          <Badge variant="secondary">{task.type.replace('_', ' ')}</Badge>
          <h2 className="mt-3 text-2xl font-bold">{task.title}</h2>
          {task.description && <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{task.description}</p>}
          {task.entityType && task.entityId && (
            <Link to={`/crm/${RECORD_PATH[task.entityType] ?? task.entityType}/${task.entityId}`} target="_blank" className="text-link mt-4 inline-flex items-center gap-1 font-semibold hover:underline">
              {task.recordName ?? t('Open record')} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          <p className="mt-2 text-sm text-muted-foreground">{task.dueAt ? `${t('Due')} ${formatDate(task.dueAt)}` : t('No due date')}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={() => complete.mutate(task.id)} disabled={complete.isPending}>
              <Check /> {t('Complete and next')}
            </Button>
            <Button variant="outline" onClick={() => setIndex(index + 1)}>
              <SkipForward /> {t('Skip')}
            </Button>
            <Button variant="ghost" onClick={onExit}>
              <X /> {t('Stop')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
